import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, CircularProgress,
  Alert, IconButton, Tooltip, Chip, Divider, Tabs, Tab,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CodeIcon from '@mui/icons-material/Code';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CcIcon from '@mui/icons-material/AlternateEmail';
import axios from 'axios';
import { buildPublicPdfUrl, buildPublicXmlUrl, buildPublicCdrUrl, obtenerTokenPublico, fmtFechaWsp, nombreTipoDocumento } from '../utils/whatsapp';

const FORMATOS = ['A4', 'A5', 'T80'];

function buildTemplate(tmpl, vars) {
  let r = tmpl;
  Object.entries(vars).forEach(([k, v]) => {
    r = r.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v ?? '');
  });
  return r;
}

function parseEmails(str) {
  return str.split(',').map(s => s.trim()).filter(Boolean);
}

const ADJ_XML_MARK = 'data-isadj-xml="1"';
const ADJ_CDR_MARK = 'data-isadj-cdr="1"';

function removeAdjLine(html, mark) {
  const escaped = mark.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return html.replace(new RegExp(`<p ${escaped}>[\\s\\S]*?<\\/p>\\n?`, 'g'), '').trimEnd();
}

function buildAdjLine(mark, content) {
  return `\n<p ${mark}>${content}</p>`;
}

// Inserta una línea en el HTML inmediatamente después del párrafo que contiene pdfUrl.
// Si no lo encuentra, lo añade al final.
function insertAdjAfterPdfParagraph(html, pdfUrl, adjLine) {
  if (!pdfUrl) return html + adjLine;
  const urlIdx = html.indexOf(pdfUrl);
  if (urlIdx !== -1) {
    const pEnd = html.indexOf('</p>', urlIdx);
    if (pEnd !== -1) {
      return html.substring(0, pEnd + 4) + adjLine + html.substring(pEnd + 4);
    }
  }
  return html + adjLine;
}

// Inserta adjLine después del párrafo marcado con mark. Retorna null si no lo encuentra.
function insertAdjAfterMark(html, mark, adjLine) {
  const escaped = mark.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const idx = html.search(new RegExp(`<p ${escaped}>`));
  if (idx !== -1) {
    const pEnd = html.indexOf('</p>', idx);
    if (pEnd !== -1) return html.substring(0, pEnd + 4) + adjLine + html.substring(pEnd + 4);
  }
  return null;
}

export default function EmailDialog({
  open, onClose,
  docTipo = 'comprobante', tipoDoc = '01',
  docId, clienteId,
  serie = '', numero = '',
  fecha = '', total = 0, moneda = 'PEN',
  clienteNombre = '',
  empresaNombre = '',
}) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tab, setTab] = useState(0); // 0=redactar, 1=preview

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const [formato, setFormato] = useState('A4');
  const [adjPdf, setAdjPdf] = useState(true);
  const [adjXml, setAdjXml] = useState(false);
  const [adjCdr, setAdjCdr] = useState(false);
  const [tokenXml, setTokenXml] = useState('');
  const [tokenCdr, setTokenCdr] = useState('');
  const [tokenPdf, setTokenPdf] = useState('');

  const [extraFiles, setExtraFiles] = useState([]);
  const fileInputRef = useRef(null);

  const [nombreEmpresa, setNombreEmpresa] = useState(empresaNombre || '');
  const [baseVars, setBaseVars] = useState({});
  const [asuntoTmpl, setAsuntoTmpl] = useState('');
  const [cuerpoTmpl, setCuerpoTmpl] = useState('');

  const inicializar = useCallback(async () => {
    if (!open || !docId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    setTab(0);
    setFormato('A4');
    setAdjPdf(true);
    setAdjXml(false);
    setAdjCdr(false);
    setTokenXml('');
    setTokenCdr('');
    setExtraFiles([]);
    setShowCc(false);
    setShowBcc(false);
    setCc('');
    setBcc('');

    try {
      // Token PDF
      const token = await obtenerTokenPublico(docTipo, docId);
      setTokenPdf(token || '');

      // Tokens XML/CDR (comprobantes y guías de remisión)
      if (docTipo === 'comprobante' || docTipo === 'guia') {
        const tipoXml = docTipo === 'guia' ? 'xml-guia' : 'xml';
        const tipoCdr = docTipo === 'guia' ? 'cdr-guia' : 'cdr';
        try {
          const [rxm, rcd] = await Promise.all([
            axios.get(`/api/wsp-config/generar-token/${tipoXml}/${docId}`),
            axios.get(`/api/wsp-config/generar-token/${tipoCdr}/${docId}`),
          ]);
          setTokenXml(rxm.data.token || '');
          setTokenCdr(rcd.data.token || '');
        } catch (_) {}
      }

      // Email y nombre del cliente
      let emailCliente = '';
      let emailContactoCliente = '';
      let nombreCliente = clienteNombre || '';
      if (clienteId) {
        try {
          const r = await axios.get(`/api/email/contacto-cliente/${clienteId}`);
          emailCliente = r.data.email || '';
          emailContactoCliente = r.data.email_contacto || '';
          if (!nombreCliente && r.data.nombre) nombreCliente = r.data.nombre;
        } catch (_) {}
      }
      setTo(emailCliente);
      if (emailContactoCliente) {
        setCc(emailContactoCliente);
        setShowCc(true);
      }

      // Nombre empresa
      let empNombre = empresaNombre || nombreEmpresa;
      if (!empNombre) {
        try {
          const ec = await axios.get('/api/empresa-config');
          empNombre = ec.data?.razon_social || ec.data?.nombre_comercial || '';
        } catch (_) {}
      }
      setNombreEmpresa(empNombre);

      // Plantilla email
      const pt = await axios.get(`/api/email/plantilla/${tipoDoc}`);
      const asuntoT = pt.data?.asunto_template || '';
      const cuerpoT = pt.data?.cuerpo_html_template || '';
      setAsuntoTmpl(asuntoT);
      setCuerpoTmpl(cuerpoT);

      // Variables
      const linkPdf = token ? buildPublicPdfUrl(docTipo, docId, token, 'A4') : '';
      const vars = {
        nombre_empresa: empNombre,
        nombre_cliente: nombreCliente,
        tipo_documento_nombre: nombreTipoDocumento(tipoDoc),
        serie,
        numero: String(numero).padStart(8, '0'),
        fecha: fmtFechaWsp(fecha),
        total: parseFloat(total || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        moneda: ({ USD: '$', EUR: '€', PEN: 'S/' })[moneda] || 'S/',
        link_pdf: linkPdf || '(link disponible al abrir)',
      };
      setBaseVars(vars);
      setSubject(buildTemplate(asuntoT, vars));
      setBodyHtml(buildTemplate(cuerpoT, vars));
    } catch (e) {
      setError('Error al cargar datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [open, docId, docTipo, tipoDoc, clienteId, clienteNombre, serie, numero, fecha, total, moneda, empresaNombre]);

  useEffect(() => { inicializar(); }, [inicializar]);

  const numStr = () => String(numero).padStart(8, '0');

  const handleFormatoChange = (f) => {
    setFormato(f);
    if (!tokenPdf) return;
    const newLink = buildPublicPdfUrl(docTipo, docId, tokenPdf, f);
    const newVars = { ...baseVars, link_pdf: newLink };
    let newBody = buildTemplate(cuerpoTmpl, newVars);
    if (adjXml && tokenXml) {
      const xmlUrl = buildPublicXmlUrl(docId, tokenXml, docTipo);
      newBody = insertAdjAfterPdfParagraph(newBody, newLink,
        buildAdjLine(ADJ_XML_MARK, `📎 <strong>XML firmado:</strong> <a href="${xmlUrl}">${serie}-${numStr()}.xml</a>`));
    }
    if (adjCdr && tokenCdr) {
      const cdrUrl = buildPublicCdrUrl(docId, tokenCdr, docTipo);
      const cdrLine = buildAdjLine(ADJ_CDR_MARK, `📦 <strong>CDR SUNAT:</strong> <a href="${cdrUrl}">R-${serie}-${numStr()}.xml</a>`);
      newBody = insertAdjAfterMark(newBody, ADJ_XML_MARK, cdrLine) ?? insertAdjAfterPdfParagraph(newBody, newLink, cdrLine);
    }
    setBodyHtml(newBody);
    setSubject(buildTemplate(asuntoTmpl, newVars));
  };

  const handleAdjXmlChange = (next) => {
    const pdfUrl = tokenPdf ? buildPublicPdfUrl(docTipo, docId, tokenPdf, formato) : '';
    setAdjXml(next);
    setBodyHtml(prev => {
      let html = removeAdjLine(prev, ADJ_XML_MARK);
      if (next && tokenXml) {
        const xmlUrl = buildPublicXmlUrl(docId, tokenXml, docTipo);
        html = insertAdjAfterPdfParagraph(html, pdfUrl,
          buildAdjLine(ADJ_XML_MARK, `📎 <strong>XML firmado:</strong> <a href="${xmlUrl}">${serie}-${numStr()}.xml</a>`));
      }
      return html;
    });
  };

  const handleAdjCdrChange = (next) => {
    const pdfUrl = tokenPdf ? buildPublicPdfUrl(docTipo, docId, tokenPdf, formato) : '';
    setAdjCdr(next);
    setBodyHtml(prev => {
      let html = removeAdjLine(prev, ADJ_CDR_MARK);
      if (next && tokenCdr) {
        const cdrUrl = buildPublicCdrUrl(docId, tokenCdr, docTipo);
        const cdrLine = buildAdjLine(ADJ_CDR_MARK, `📦 <strong>CDR SUNAT:</strong> <a href="${cdrUrl}">R-${serie}-${numStr()}.xml</a>`);
        html = insertAdjAfterMark(html, ADJ_XML_MARK, cdrLine) ?? insertAdjAfterPdfParagraph(html, pdfUrl, cdrLine);
      }
      return html;
    });
  };

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setExtraFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeExtraFile = (idx) => {
    setExtraFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleEnviar = async () => {
    const toList = parseEmails(to);
    if (!toList.length) { setError('Ingrese al menos un destinatario'); return; }
    if (!subject.trim()) { setError('El asunto no puede estar vacío'); return; }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('to', to);
      if (cc && showCc) formData.append('cc', cc);
      if (bcc && showBcc) formData.append('bcc', bcc);
      formData.append('subject', subject);
      formData.append('html', bodyHtml);
      formData.append('docTipo', docTipo);
      formData.append('docId', String(docId));
      formData.append('formato', formato);
      formData.append('adjPdf', String(adjPdf));
      formData.append('adjXml', String(adjXml && !!tokenXml));
      formData.append('adjCdr', String(adjCdr && !!tokenCdr));
      extraFiles.forEach(f => formData.append('adjuntos_extra', f, f.name));

      await axios.post('/api/email/enviar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(`Correo enviado correctamente a ${toList.join(', ')}`);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  const numeroFormateado = `${serie}-${String(numero).padStart(8, '0')}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 500 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <EmailIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
            Enviar por correo electrónico
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {nombreTipoDocumento(tipoDoc)} {numeroFormateado}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Para / CC / BCC */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  size="small"
                  label="Para"
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  placeholder="correo@ejemplo.com, otro@ejemplo.com"
                  helperText="Puede ingresar varios correos separados por coma"
                  sx={{ flex: 1 }}
                  disabled={sending}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
                  <Tooltip title="Agregar CC">
                    <Chip
                      label="CC"
                      size="small"
                      icon={<CcIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => setShowCc(v => !v)}
                      color={showCc ? 'primary' : 'default'}
                      variant={showCc ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                  <Tooltip title="Agregar BCC (con copia oculta)">
                    <Chip
                      label="BCC"
                      size="small"
                      icon={<CcIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => setShowBcc(v => !v)}
                      color={showBcc ? 'primary' : 'default'}
                      variant={showBcc ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                </Box>
              </Box>
              {showCc && (
                <TextField
                  size="small"
                  label="CC (con copia)"
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  disabled={sending}
                />
              )}
              {showBcc && (
                <TextField
                  size="small"
                  label="BCC (copia oculta)"
                  value={bcc}
                  onChange={e => setBcc(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  disabled={sending}
                />
              )}
              <TextField
                size="small"
                label="Asunto"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                fullWidth
                disabled={sending}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Formato + Adjuntos */}
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  FORMATO Y ADJUNTOS
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                {FORMATOS.map(f => (
                  <Chip
                    key={f}
                    label={f}
                    size="small"
                    onClick={() => handleFormatoChange(f)}
                    color={formato === f ? 'primary' : 'default'}
                    variant={formato === f ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer', fontWeight: formato === f ? 'bold' : 'normal', minWidth: 44 }}
                    disabled={sending}
                  />
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Chip
                  icon={<PictureAsPdfIcon />}
                  label="PDF"
                  size="small"
                  onClick={() => setAdjPdf(v => !v)}
                  color={adjPdf ? 'error' : 'default'}
                  variant={adjPdf ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                  disabled={sending}
                />
                {(docTipo === 'comprobante' || docTipo === 'guia') && tokenXml && (
                  <Chip
                    icon={<CodeIcon />}
                    label="XML"
                    size="small"
                    onClick={() => handleAdjXmlChange(!adjXml)}
                    color={adjXml ? 'success' : 'default'}
                    variant={adjXml ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    disabled={sending}
                  />
                )}
                {(docTipo === 'comprobante' || docTipo === 'guia') && tokenCdr && (
                  <Chip
                    icon={<FolderZipIcon />}
                    label="CDR"
                    size="small"
                    onClick={() => handleAdjCdrChange(!adjCdr)}
                    color={adjCdr ? 'success' : 'default'}
                    variant={adjCdr ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    disabled={sending}
                  />
                )}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileAdd}
                />
                <Tooltip title="Adjuntar archivos adicionales">
                  <Chip
                    icon={<AttachFileIcon />}
                    label={extraFiles.length > 0 ? `+${extraFiles.length}` : 'Adjuntar'}
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    color={extraFiles.length > 0 ? 'warning' : 'default'}
                    variant={extraFiles.length > 0 ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                    disabled={sending}
                  />
                </Tooltip>
              </Box>
              {extraFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                  {extraFiles.map((f, idx) => (
                    <Chip
                      key={idx}
                      label={f.name}
                      size="small"
                      onDelete={() => removeExtraFile(idx)}
                      variant="outlined"
                      sx={{ maxWidth: 180, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Editor / Preview */}
            <Box sx={{ mb: 1 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 32, mb: 1 }}>
                <Tab label="Redactar" sx={{ minHeight: 32, py: 0.5, fontSize: '0.8rem' }} />
                <Tab label="Vista previa" sx={{ minHeight: 32, py: 0.5, fontSize: '0.8rem' }} />
              </Tabs>
              {tab === 0 ? (
                <TextField
                  multiline
                  rows={9}
                  fullWidth
                  size="small"
                  value={bodyHtml}
                  onChange={e => setBodyHtml(e.target.value)}
                  placeholder="Cuerpo del correo en HTML..."
                  disabled={sending}
                  inputProps={{ style: { fontSize: '0.8rem', fontFamily: 'monospace', lineHeight: 1.5 } }}
                  helperText="Puede usar HTML para formato. Las variables {{nombre_cliente}}, {{link_pdf}}, etc. ya están reemplazadas."
                />
              ) : (
                <Box sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2,
                  minHeight: 200, maxHeight: 280, overflowY: 'auto',
                  bgcolor: 'background.paper', fontSize: '0.88rem',
                }}
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={sending}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleEnviar}
          disabled={loading || sending || !!success}
        >
          {sending ? 'Enviando...' : success ? 'Enviado' : 'Enviar correo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
